import { MouseEvent as ReactMouseEvent } from "react";
import styled from "styled-components";
import { Flex, Text } from "rebass/styled-components";
import { FaPlay, FaHeart, FaComment } from "react-icons/fa";

import { formatNumber } from "MVVM/Utilities/Clip";

interface IClipStatBarProps {
  views?: number;
  likeCount?: number;
  commentCount?: number;
  isLiked?: boolean;
  onView?: () => void;
  onLike?: () => void;
  onComment?: () => void;
}

// Reusable view/like/comment overlay. Icons always show; the number is hidden
// when the count is 0 (per product ask). All three cells are actionable: play
// opens the clip, heart likes, comment opens the comments thread.
export const ClipStatBar = ({
  views = 0,
  likeCount = 0,
  commentCount = 0,
  isLiked = false,
  onView,
  onLike,
  onComment,
}: IClipStatBarProps) => {
  const guard = (handler?: () => void) => (event: ReactMouseEvent) => {
    if (!handler) return;
    event.stopPropagation();
    handler();
  };

  return (
    <StatContainer>
      <StatCell as="button" $clickable onClick={guard(onView)}>
        <ViewIcon />
        {views > 0 && <StatText>{formatNumber(views)}</StatText>}
      </StatCell>
      <StatCell as="button" $clickable onClick={guard(onLike)}>
        <LikeIcon $active={isLiked} />
        {likeCount > 0 && <StatText>{formatNumber(likeCount)}</StatText>}
      </StatCell>
      <StatCell as="button" $clickable onClick={guard(onComment)}>
        <CommentIcon />
        {commentCount > 0 && <StatText>{formatNumber(commentCount)}</StatText>}
      </StatCell>
    </StatContainer>
  );
};

const StatContainer = styled(Flex)`
  align-items: center;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(3px);
  border-radius: 999px;
  gap: 14px;
  padding: 7px 14px;
  pointer-events: auto;
`;

const StatCell = styled(Flex)<{ $clickable?: boolean }>`
  align-items: center;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.ice};
  cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};
  gap: 6px;
  padding: 0;
  transition: transform 0.15s ease;

  &:hover {
    transform: ${({ $clickable }) => ($clickable ? "scale(1.08)" : "none")};
  }
`;

const StatText = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 13px;
  font-weight: 700;
`;

const ViewIcon = styled(FaPlay)`
  color: ${({ theme }) => theme.colors.ice};
  height: 14px;
  width: 14px;
`;

const LikeIcon = styled(FaHeart)<{ $active?: boolean }>`
  color: ${({ theme, $active }) =>
    $active ? theme.colors.heartRed : theme.colors.chalk};
  height: 15px;
  width: 15px;
`;

const CommentIcon = styled(FaComment)`
  color: ${({ theme }) => theme.colors.ice};
  height: 15px;
  width: 15px;
`;
